import { useTranslations } from "next-intl";
import type { DietCatalog, DietCatalogDetail } from "@/features/diets/types";
import { getLocaleForPathname } from "@/i18n/navigation";
import { useDietDates } from "@/lib/hooks/use-diet-dates";
import { getLocalePrice } from "@/lib/utilities";

const formatPrice = (price: number | null) =>
  getLocalePrice({
    price: price ?? 0,
    options: { trailingZeroDisplay: "stripIfInteger" },
  });

/**
 * Label of the delivery row on the diet card — the next possible delivery day
 * plus the catering's delivery cost, e.g. "Na czwartek, darmowa dostawa" or
 * "Na sobotę, dostawa od 5 zł". Both parts come from the catalog; when the
 * catering exposes no cost the day alone is shown.
 *
 * The day name is translated rather than formatted with date-fns: Polish and
 * Czech need the accusative form after "na" / "w", which `format(…, "eeee")`
 * does not give.
 */
export const useDietDeliveryLabel = ({
  diet,
}: {
  diet: DietCatalog | DietCatalogDetail;
}) => {
  const t = useTranslations("diets.deliveryRow");
  const tWeekdays = useTranslations("diets.weekdaysAccusative");
  const locale = getLocaleForPathname();
  const { nextPossibleDay } = useDietDates({
    diet,
    saturdays: true,
    sundays: true,
    locale,
  });

  const day = tWeekdays(String(nextPossibleDay(new Date()).getDay()));
  const { type, price } = diet.catering.deliveryCosts;

  const cost = (() => {
    switch (type) {
      case "free":
        return t("costFree");
      case "from":
        return t("costFrom", { price: formatPrice(price) });
      case "equal":
        return t("costEqual", { price: formatPrice(price) });
      default:
        return null;
    }
  })();

  return {
    day,
    label: cost ? t("withCost", { day, cost }) : t("dayOnly", { day }),
  };
};
